// Performance Management System (PMS) Page
// Phase A: Scoring + Coaching + Leaderboard
// Phase B: Config Panel + Audits + Anomalies
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Trophy, TrendingUp, TrendingDown, Minus, BarChart3,
  Loader2, Zap, MessageSquare, ChevronRight, Settings
} from 'lucide-react';

// 21 Oct 2026 (security follow-up, item 13): PMS is frozen on its last stored
// snapshot — Compute Scores, AI Coaching generation, Audits and Anomalies are
// all removed from this page (their backend endpoints now return 403; every
// PMS metric is derived from offers/purchases, which are retired).
export default function PMSPage() {
  const { user } = useAuth();
  const [myScore, setMyScore] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [coaching, setCoaching] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const isAdmin = user?.roles?.includes('super_admin') || user?.roles?.includes('admin');

  const fetchMyScore = useCallback(async () => {
    try {
      const res = await api.get('/pms/score/me');
      setMyScore(res.data);
      setCoaching(res.data.coaching);
    } catch (e) {}
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/pms/score/leaderboard');
      setLeaderboard(res.data.leaderboard || []);
    } catch (e) {}
  }, [isAdmin]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/pms/config');
      setConfig(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchMyScore(), fetchLeaderboard(), fetchConfig()]);
      setLoading(false);
    };
    load();
  }, [fetchMyScore, fetchLeaderboard, fetchConfig]);

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/pms/config', config);
      toast.success('PMS configuration saved');
      setConfigOpen(false);
    } catch (e) { toast.error('Failed to save config'); }
    finally { setSavingConfig(false); }
  };

  const viewUserDetail = async (uid) => {
    try {
      const res = await api.get(`/pms/score/${uid}`);
      setSelectedDetail(res.data);
    } catch (e) { toast.error('Failed'); }
  };

  const getClassBadge = (cls) => {
    const styles = { HIGH_PERFORMER: 'bg-green-500 text-white', STABLE: 'bg-blue-500 text-white', NEEDS_REVIEW: 'bg-yellow-500 text-white', CRITICAL: 'bg-red-500 text-white' };
    return <Badge className={styles[cls] || 'bg-gray-500'}>{cls?.replace(/_/g, ' ')}</Badge>;
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend.length < 2) return <Minus className="h-4 w-4 text-gray-400" />;
    const last = trend[trend.length - 1]?.score_total || 0;
    const prev = trend[trend.length - 2]?.score_total || 0;
    if (last > prev) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (last < prev) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getScoreColor = (s) => s >= 80 ? 'text-green-600' : s >= 60 ? 'text-blue-600' : s >= 40 ? 'text-yellow-600' : 'text-red-600';
  const getBarColor = (s) => s >= 80 ? 'bg-green-500' : s >= 60 ? 'bg-blue-500' : s >= 40 ? 'bg-yellow-500' : 'bg-red-500';

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const score = myScore?.score;
  const breakdown = score?.score_breakdown || {};

  return (
    <div className="space-y-6" data-testid="pms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Trophy className="h-6 w-6 text-primary" /> Performance Management</h1>
          <p className="text-muted-foreground">Last computed performance snapshot — scoring is paused (Offers & Purchases retired)</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={() => { fetchConfig(); setConfigOpen(true); }} variant="outline" size="sm" data-testid="pms-config-btn">
              <Settings className="h-4 w-4 mr-1" /> Configure
            </Button>
          )}
        </div>
      </div>

      {/* My Score Card */}
      {score && (
        <Card className="border-2" data-testid="my-score-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className={`text-5xl font-bold ${getScoreColor(score.score_total)}`} data-testid="my-score-value">{score.score_total}</div>
                <div><p className="text-sm text-muted-foreground">out of 100</p>{getClassBadge(score.classification)}</div>
                {getTrendIcon(myScore?.trend)}
              </div>
              <div className="text-sm text-muted-foreground text-right">
                <p>Period: {score.period_start} to {score.period_end}</p>
                <p>{score.raw_metrics?.total_offers || 0} offers, {score.raw_metrics?.conversions || 0} conversions</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(breakdown).map(([key, val]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-mono font-semibold">{val.score?.toFixed(0)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${getBarColor(val.score)}`} style={{ width: `${Math.min(val.score, 100)}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground">{val.detail}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={isAdmin ? "leaderboard" : "coaching"} className="space-y-4">
        <TabsList>
          {isAdmin && <TabsTrigger value="leaderboard" data-testid="leaderboard-tab"><BarChart3 className="h-4 w-4 mr-1" /> Leaderboard</TabsTrigger>}
          <TabsTrigger value="coaching" data-testid="coaching-tab"><MessageSquare className="h-4 w-4 mr-1" /> AI Coaching</TabsTrigger>
        </TabsList>

        {/* Leaderboard */}
        {isAdmin && (
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader><CardTitle className="text-base">Team Leaderboard</CardTitle></CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><Trophy className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No PMS snapshot on record.</p></div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-12">Rank</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                        <TableHead className="text-center">Score</TableHead><TableHead>Status</TableHead>
                        <TableHead className="text-right">Offers</TableHead><TableHead className="text-right">Conv %</TableHead>
                        <TableHead className="text-right">Revenue</TableHead><TableHead>Actions</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {leaderboard.map((u, i) => (
                          <TableRow key={u.user_id} data-testid={`lb-row-${u.user_id}`}>
                            <TableCell className="font-bold text-center">{i === 0 ? '1' : i === 1 ? '2' : i === 2 ? '3' : i + 1}</TableCell>
                            <TableCell className="font-medium">{u.user_name || 'N/A'}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{u.user_email}</TableCell>
                            <TableCell className="text-center"><span className={`text-lg font-bold ${getScoreColor(u.score_total)}`}>{u.score_total}</span></TableCell>
                            <TableCell>{getClassBadge(u.classification)}</TableCell>
                            <TableCell className="text-right font-mono">{u.raw_metrics?.total_offers || 0}</TableCell>
                            <TableCell className="text-right font-mono">{u.score_breakdown?.conversion_rate?.value || 0}%</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(u.raw_metrics?.total_revenue || 0)}</TableCell>
                            <TableCell><Button variant="ghost" size="sm" onClick={() => viewUserDetail(u.user_id)}><ChevronRight className="h-4 w-4" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* AI Coaching (read-only — last generated coaching, if any) */}
        <TabsContent value="coaching">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> AI Performance Coaching</CardTitle></CardHeader>
            <CardContent>
              {!coaching ? (
                <div className="text-center py-8 text-muted-foreground"><MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No coaching on record.</p></div>
              ) : (
                <div className="space-y-4" data-testid="coaching-content">
                  {coaching.summary && <div className="p-4 bg-muted rounded-lg"><p className="text-sm font-medium">{coaching.summary}</p></div>}
                  {coaching.strengths?.length > 0 && (
                    <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                      <p className="text-xs font-semibold text-green-700 mb-2">Strengths</p>
                      <ul className="text-sm space-y-1 list-disc pl-4">{coaching.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {coaching.improvements?.length > 0 && (
                    <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Actions for Next Week</p>
                      <ul className="text-sm space-y-1 list-disc pl-4">{coaching.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                  {coaching.warning && <div className="p-4 border border-red-200 bg-red-50 rounded-lg"><p className="text-xs font-semibold text-red-700 mb-2">Warning</p><p className="text-sm text-red-800">{coaching.warning}</p></div>}
                  {coaching.next_week_focus && <div className="p-3 bg-muted rounded-lg"><p className="text-xs text-muted-foreground font-semibold">Focus Area</p><p className="text-sm font-medium">{coaching.next_week_focus}</p></div>}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <Dialog open={!!selectedDetail} onOpenChange={() => setSelectedDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDetail?.score?.user_name || 'User'} — PMS Detail</DialogTitle>
            <DialogDescription>{selectedDetail?.score?.user_email}</DialogDescription>
          </DialogHeader>
          {selectedDetail?.score && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className={`text-4xl font-bold ${getScoreColor(selectedDetail.score.score_total)}`}>{selectedDetail.score.score_total}</span>
                {getClassBadge(selectedDetail.score.classification)}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(selectedDetail.score.score_breakdown || {}).map(([key, val]) => (
                  <div key={key} className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-semibold">{val.score?.toFixed(0)}/100</p>
                    <p className="text-xs text-muted-foreground">{val.detail}</p>
                  </div>
                ))}
              </div>
              {selectedDetail.coaching && <div className="p-4 bg-muted rounded-lg"><p className="text-xs font-semibold mb-2">AI Coaching</p><p className="text-sm">{selectedDetail.coaching.summary}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PMS Config Modal */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> PMS Configuration</DialogTitle>
            <DialogDescription>Adjust scoring weights, targets, and thresholds</DialogDescription>
          </DialogHeader>
          {config && (
            <div className="space-y-6">
              {/* Weights */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Metric Weights (must sum to 100)</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(config.weights || {}).map(([key, val]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input type="number" min="0" max="100" value={val}
                        onChange={e => setConfig(prev => ({ ...prev, weights: { ...prev.weights, [key]: parseInt(e.target.value) || 0 } }))}
                        className="h-8 font-mono" data-testid={`weight-${key}`} />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Total: {Object.values(config.weights || {}).reduce((a, b) => a + b, 0)}
                </p>
              </div>

              {/* Targets */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Targets (per period)</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(config.targets || {}).map(([key, val]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input type="number" min="0" value={val}
                        onChange={e => setConfig(prev => ({ ...prev, targets: { ...prev.targets, [key]: parseFloat(e.target.value) || 0 } }))}
                        className="h-8 font-mono" data-testid={`target-${key}`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Thresholds */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Classification Thresholds</h3>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(config.thresholds || {}).map(([key, val]) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input type="number" min="0" max="100" value={val}
                        onChange={e => setConfig(prev => ({ ...prev, thresholds: { ...prev.thresholds, [key]: parseInt(e.target.value) || 0 } }))}
                        className="h-8 font-mono" data-testid={`threshold-${key}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="save-pms-config">
              {savingConfig ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
